// src/pages/admin/AdminDashboard.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { logout } from "../../services/authService";
import {
  createManualJob,
  getPendingJobs,
  approveJob,
  deleteJob,
  getLocalJobs,
  bulkApproveJobs,
  bulkDeleteJobs,
} from "../../services/firestoreService";
import AdminJobPostForm from "../../components/admin/AdminJobPostForm";
import JobCard from "../../components/jobs/JobCard";
import Button from "../../components/ui/Button";
import styles from "./AdminDashboard.module.css";

export default function AdminDashboard() {
  const { userRole } = useAuth();
  const navigate = useNavigate();

  const [tab,           setTab]           = useState("post");
  const [pendingJobs,   setPendingJobs]   = useState([]);
  const [manualJobs,    setManualJobs]    = useState([]);
  const [selected,      setSelected]      = useState(new Set());
  const [loading,       setLoading]       = useState(true);
  const [submitting,    setSubmitting]    = useState(false);
  const [message,       setMessage]       = useState({ text: "", type: "success" });
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    if (userRole !== "admin") { navigate("/dashboard"); return; }
    loadJobs();
  }, [userRole, navigate]);

  const flash = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "success" }), 4000);
  };

  const loadJobs = async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const [pending, manual] = await Promise.all([
        getPendingJobs(),
        getLocalJobs({ approved: true, maxLimit: 20, source: "Manual" }),
      ]);
      setPendingJobs(pending);
      setManualJobs(manual);
    } catch (err) {
      flash("Failed to load jobs: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => setSelected(prev => {
    const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s;
  });
  const toggleSelectAll = () => setSelected(
    selected.size === pendingJobs.length ? new Set() : new Set(pendingJobs.map(j => j.id))
  );
  const handleBulkApprove = async () => {
    if (!selected.size) return;
    try {
      await bulkApproveJobs([...selected]);
      flash(`${selected.size} job${selected.size !== 1 ? "s" : ""} approved.`);
      loadJobs();
    } catch (err) { flash("Bulk approve failed: " + err.message, "error"); }
  };
  const handleBulkDelete = async () => {
    if (!selected.size) return;
    try {
      await bulkDeleteJobs([...selected]);
      flash(`${selected.size} job${selected.size !== 1 ? "s" : ""} deleted.`);
      loadJobs();
    } catch (err) { flash("Bulk delete failed: " + err.message, "error"); }
  };

  const handlePostJob = async (jobData) => {
    setSubmitting(true);
    try {
      // FIX: createManualJob now uses addDoc — no more documentId() misuse
      await createManualJob(jobData);
      flash("✓ Job posted successfully!");
      setTab("manual");
      loadJobs();
    } catch (err) {
      flash("Failed to post job: " + err.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (jobId) => {
    try {
      await approveJob(jobId, true);
      flash("Job approved.");
      loadJobs();
    } catch (err) {
      flash("Failed to approve: " + err.message, "error");
    }
  };

  const handleDeleteClick = (jobId) => {
    if (confirmDelete === jobId) {
      executeDelete(jobId);
    } else {
      setConfirmDelete(jobId);
      setTimeout(() => setConfirmDelete(c => c === jobId ? null : c), 4000);
    }
  };

  const executeDelete = async (jobId) => {
    setConfirmDelete(null);
    try {
      await deleteJob(jobId);
      flash("Job deleted.");
      loadJobs();
    } catch (err) {
      flash("Failed to delete: " + err.message, "error");
    }
  };

  if (loading) return <div className={styles.loading}>Loading admin portal…</div>;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.brand} onClick={() => navigate("/dashboard")}>
          <span className={styles.logo}>GS</span>
          <span className={styles.appName}>Admin Portal</span>
        </div>
        <nav className={styles.nav}>
          {[
            { id: "post",    label: "Post Job" },
            { id: "pending", label: `Pending (${pendingJobs.length})` },
            { id: "manual",  label: `Manual (${manualJobs.length})` },
          ].map(({ id, label }) => (
            <button
              key={id}
              className={`${styles.navLink} ${tab === id ? styles.active : ""}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
          <button className={styles.navLink} onClick={() => navigate("/admin/local-gigs")}>
            Local Gigs
          </button>
        </nav>
        <Button variant="ghost" size="sm" onClick={async () => { await logout(); navigate("/login"); }}>
          Logout
        </Button>
      </header>

      <main className={styles.main}>
        {message.text && (
          <div className={`${styles.message} ${message.type === "error" ? styles.messageError : ""}`}>
            {message.text}
          </div>
        )}

        {tab === "post" && (
          <div className={styles.section}>
            <h1>Post New Job</h1>
            <p style={{ color: "var(--color-text-muted)", fontSize: 14, marginBottom: "var(--space-lg)" }}>
              Jobs posted here are immediately live and visible to all users.
            </p>
            <AdminJobPostForm onSubmit={handlePostJob} submitting={submitting} />
          </div>
        )}

        {tab === "pending" && (
          <div className={styles.section}>
            <h1>Pending Approval ({pendingJobs.length})</h1>
            <p style={{ color: "var(--color-text-muted)", fontSize: 14, marginBottom: "var(--space-lg)" }}>
              Jobs scraped from SA marketplaces, awaiting your review.
            </p>
            {pendingJobs.length === 0 ? (
              <div className={styles.empty}>No pending jobs. Run the scraper to populate this.</div>
            ) : (
              <>
                <div className={styles.bulkBar}>
                  <label className={styles.checkAll}>
                    <input type="checkbox"
                      checked={selected.size === pendingJobs.length && pendingJobs.length > 0}
                      onChange={toggleSelectAll} />
                    {selected.size > 0 ? `${selected.size} selected` : "Select all"}
                  </label>
                  {selected.size > 0 && (
                    <div className={styles.bulkActions}>
                      <Button onClick={handleBulkApprove} variant="primary" size="sm">Approve {selected.size}</Button>
                      <Button onClick={handleBulkDelete} variant="destructive" size="sm">Delete {selected.size}</Button>
                    </div>
                  )}
                </div>
                <div className={styles.grid}>
                  {pendingJobs.map(job => (
                    <div key={job.id} className={`${styles.jobCard} ${selected.has(job.id) ? styles.jobCardSelected : ""}`}>
                      <label className={styles.selectCheck}>
                        <input type="checkbox" checked={selected.has(job.id)} onChange={() => toggleSelect(job.id)} />
                      </label>
                      <JobCard job={job} adminView />
                      <div className={styles.actions}>
                        <Button onClick={() => handleApprove(job.id)} size="sm" variant="primary">Approve</Button>
                        <Button onClick={() => handleDeleteClick(job.id)} variant="destructive" size="sm">
                          {confirmDelete === job.id ? "Confirm?" : "Delete"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "manual" && (
          <div className={styles.section}>
            <h1>Manually Posted Jobs ({manualJobs.length})</h1>
            {manualJobs.length === 0 ? (
              <div className={styles.empty}>
                No manual jobs yet. Use the <button className={styles.inlineLink} onClick={() => setTab("post")}>Post Job</button> tab to add some.
              </div>
            ) : (
              <div className={styles.grid}>
                {manualJobs.map(job => (
                  <div key={job.id} className={styles.jobCard}>
                    <JobCard job={job} />
                    <div className={styles.actions}>
                      <Button onClick={() => handleDeleteClick(job.id)} variant="destructive" size="sm">
                        {confirmDelete === job.id ? "Confirm?" : "Delete"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
